class ProgressiveLoader {
  constructor(options = {}) {
    // 默认配置
    this.config = {
      selector: '.lazy-load',     // 图片选择器
      rootMargin: '200px',        // 视口偏移量
      threshold: 0.01,            // 交叉阈值
      loadedClass: 'loaded',      // 加载完成后的 CSS 类名
      usePreloader: true,         // 是否使用 new Image() 预加载 (平滑过渡)
      onLoaded: null,             // 加载成功回调 (el) => {}
      onError: null,              // 加载失败回调 (el, err) => {}
      ...options                  // 合并用户配置
    };

    this.observer = null;
    this.init();
  }

  init() {
    // 如果浏览器不支持 IntersectionObserver，直接加载所有图片 (降级处理)
    if (!('IntersectionObserver' in window)) {
      this.loadAllImmediately();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this._handleIntersection(entries),
      {
        rootMargin: this.config.rootMargin,
        threshold: this.config.threshold,
      }
    );

    this.update(); // 初始扫描
  }

  // 处理交叉检测
  _handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const el = entry.target;
        this._loadImage(el);
        this.observer.unobserve(el); // 停止观察已处理的元素
      }
    });
  }

  // 核心加载逻辑
  // 核心加载逻辑
  _loadImage(el) {
    const parent = el.parentElement;
    const isPicture = parent.tagName === 'PICTURE';

    // 1. 如果包裹在 <picture> 里，必须先处理所有的 <source> 兄弟元素
    if (isPicture) {
        const sources = parent.querySelectorAll('source');
        sources.forEach(source => {
            const dataSrcset = source.dataset.srcset;
            if (dataSrcset) {
                source.srcset = dataSrcset; 
            }
        });
    }

    // 2. 获取所有可能的数据属性
    const src = el.dataset.src;
    const srcset = el.dataset.srcset;
    const bgSrc = el.dataset.bg; 
    const bgPc = el.dataset.bgPc;       // ⬆️ 提到前面来
    const bgMobile = el.dataset.bgMobile; // ⬆️ 提到前面来

    // 3. 修正判断条件：只要满足任意一种情况，就继续执行
    if (!src && !srcset && !bgSrc && !bgPc && !bgMobile) return;

    // 4. 处理普通图片 (src/srcset) 逻辑
    if (this.config.usePreloader && (src || srcset)) {
      const loader = new Image();
      if (srcset) loader.srcset = srcset;
      if (src) loader.src = src;
      loader.onload = () => this._applyAttributes(el, src, srcset);
      loader.onerror = (err) => this._handleError(el, err);
    } 
    // 5. 处理简单背景图 (data-bg)
    else if (bgSrc) {
       el.style.backgroundImage = `url(${bgSrc})`;
       this._applyLoadedClass(el);
    }
    // 6. 处理普通图片无预加载情况
    else if (src || srcset) {
       this._applyAttributes(el, src, srcset);
    }

    // 7. === 处理响应式背景图逻辑 ===
    // (这段逻辑现在可以正常执行了)
    if (bgPc || bgMobile) {
        const isMobile = window.innerWidth <= 768; 
        // 优先取对应端图片，如果没有则取另一端兜底
        const targetSrc = (isMobile && bgMobile) ? bgMobile : (bgPc || bgMobile);

        const loader = new Image();
        loader.src = targetSrc;

        loader.onload = () => {
            if (bgPc) el.style.setProperty('--bg-desktop', `url(${bgPc})`);
            if (bgMobile) el.style.setProperty('--bg-mobile', `url(${bgMobile})`);
            this._applyLoadedClass(el);
        };
    }
  }

  // 应用属性到真实 DOM
  _applyAttributes(el, src, srcset) {
    if (srcset) el.srcset = srcset;
    if (src) el.src = src;
    
    this._applyLoadedClass(el);
  }

  // 添加完成类名并执行回调
  _applyLoadedClass(el) {
    requestAnimationFrame(() => {
      el.classList.add(this.config.loadedClass);
      if (typeof this.config.onLoaded === 'function') {
        this.config.onLoaded(el);
      }
    });
  }

  // 错误处理
  _handleError(el, err) {
    console.error('Image load failed:', el, err);
    if (typeof this.config.onError === 'function') {
      this.config.onError(el, err);
    }
  }

  // 公共方法：更新观察列表 (用于 Ajax 加载新内容后调用)
  update() {
    if (!this.observer) return;
    
    const images = document.querySelectorAll(this.config.selector);
    images.forEach((img) => {
      // 避免重复观察已经处理过的图片
      if (!img.classList.contains(this.config.loadedClass)) {
        this.observer.observe(img);
      }
    });
  }

  // 降级处理：立即加载所有
  loadAllImmediately() {
    const images = document.querySelectorAll(this.config.selector);
    images.forEach(el => this._loadImage(el));
  }

  // 销毁实例
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}